import PageLayout from "@/components/feature/PageLayout";

export default function GoodFaithEstimate() {
  return (
    <>
      <title>Good Faith Estimate | Perspective Therapy No Surprises Act</title>
      <meta name="description" content="Your right to a Good Faith Estimate under the No Surprises Act. Request an estimate for therapy services at Perspective Therapy in Larchmont, NY." />
      <PageLayout>
        <section className="w-full py-14 md:py-24">
          <div className="w-full px-6 md:px-12 lg:px-20 max-w-3xl mx-auto">
            <h1 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-3">
              Good Faith Estimate
            </h1>
            <p className="font-manrope text-[#2B2B2B]/60 text-sm md:text-base mb-10 md:mb-12">
              Your right to a Good Faith Estimate under the No Surprises Act
            </p>

            <div className="flex flex-col gap-10 md:gap-12 font-manrope text-[15px] text-[#2B2B2B]/80 leading-relaxed">
              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  What Is a Good Faith Estimate?
                </h2>
                <p className="mb-4">
                  Under the No Surprises Act, health care providers are required to give individuals who do not have insurance or who are not using insurance an estimate of expected charges for medical services. This is called a Good Faith Estimate (GFE).
                </p>
                <p>
                  As a mental health counseling practice, Perspective Therapy Mental Health Counseling, PLLC is committed to transparency in pricing and your right to know what your care may cost before you receive it.
                </p>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  Who Can Request a Good Faith Estimate?
                </h2>
                <p className="mb-4">
                  You have the right to receive a Good Faith Estimate for the total expected cost of any non-emergency health care services if:
                </p>
                <ul className="list-disc pl-5 mb-4 flex flex-col gap-1">
                  <li>You do not have health insurance coverage.</li>
                  <li>You are using health insurance, but the service is not covered by your plan.</li>
                  <li>You choose not to use your health insurance for the service.</li>
                </ul>
                <p>
                  This includes related costs such as therapy sessions, assessments, consultations, and any other mental health services provided by our practice.
                </p>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  How to Request a Good Faith Estimate
                </h2>
                <p className="mb-4">
                  You can request a Good Faith Estimate before you schedule a service. We encourage you to ask for this estimate at least 3 business days before your scheduled appointment to ensure you receive it in a timely manner.
                </p>
                <p className="mb-4">To request a Good Faith Estimate, you may:</p>
                <ul className="list-disc pl-5 mb-4 flex flex-col gap-1">
                  <li>Email us at <a href="mailto:Info@perspectivetherapyNY.com" className="text-[#3C6FF8] hover:underline">Info@perspectivetherapyNY.com</a></li>
                  <li>Call us at <a href="tel:+18389108150" className="text-[#3C6FF8] hover:underline">(838) 910-8150</a></li>
                  <li>Ask during your initial consultation or inquiry</li>
                </ul>
                <p>
                  Your estimate will include the expected charges for the specific services you are seeking, based on the information available at the time of the request.
                </p>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  What the Good Faith Estimate Includes
                </h2>
                <p className="mb-4">
                  Your Good Faith Estimate will include a detailed list of expected charges for:
                </p>
                <ul className="list-disc pl-5 mb-4 flex flex-col gap-1">
                  <li>Individual therapy sessions</li>
                  <li>Couples therapy sessions</li>
                  <li>Teen therapy sessions</li>
                  <li>Initial consultations or intake sessions</li>
                  <li>Any other scheduled services you request</li>
                </ul>
                <p>
                  The estimate will clearly state the service description, the expected charge per session, and the estimated total cost based on the expected frequency or duration of treatment.
                </p>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  If Your Bill Exceeds the Estimate
                </h2>
                <p className="mb-4">
                  If you receive a bill that is at least $400 more than your Good Faith Estimate, you may dispute the charges through the patient-provider dispute resolution process.
                </p>
                <p className="mb-4">
                  You will not be required to pay the disputed amount while the dispute is being reviewed. To initiate a dispute:
                </p>
                <ol className="list-decimal pl-5 mb-4 flex flex-col gap-1">
                  <li>Contact our office to discuss the discrepancy.</li>
                  <li>If unresolved, you may file a dispute with the U.S. Department of Health and Human Services (HHS).</li>
                </ol>
                <p>
                  For more information or to start a dispute, visit the <a href="https://www.cms.gov/nosurprises" target="_blank" rel="nofollow" className="text-[#3C6FF8] hover:underline">Centers for Medicare & Medicaid Services (CMS) No Surprises Act page</a> or call 1-800-985-3059.
                </p>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  Additional Resources
                </h2>
                <p className="mb-4">
                  The following resources provide additional information about your rights under the No Surprises Act and the Good Faith Estimate requirement:
                </p>
                <ul className="list-disc pl-5 flex flex-col gap-1">
                  <li>
                    <a href="https://www.cms.gov/nosurprises" target="_blank" rel="nofollow" className="text-[#3C6FF8] hover:underline">CMS No Surprises Act Overview</a>
                  </li>
                  <li>
                    <a href="https://www.dfs.ny.gov/consumers/health_insurance/surprise_medical_bills" target="_blank" rel="nofollow" className="text-[#3C6FF8] hover:underline">New York State Department of Financial Services: Surprise Medical Bills</a>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-fraunces text-[#2B2B2B] text-xl md:text-2xl font-medium mb-3">
                  Contact Us
                </h2>
                <p className="mb-2">
                  If you have any questions about your Good Faith Estimate or your rights under the No Surprises Act, please reach out to us.
                </p>
                <p className="mb-1">
                  Email: <a href="mailto:Info@perspectivetherapyNY.com" className="text-[#3C6FF8] hover:underline">Info@perspectivetherapyNY.com</a>
                </p>
                <p>
                  Phone: <a href="tel:+18389108150" className="text-[#3C6FF8] hover:underline">(838) 910-8150</a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}